import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import {
  IPoderCalorificoNomination,
  IPoderCalorificoNominationQuery,
  IPoderCalorificoNominationOptions,
  IPoderCalorificoNominationUploadResponse,
  IPoderCalorificoResponse,
} from '@/app/core/interfaces/poder-calorifico-nomination.interface';
import { AppSettings } from '@/app/core/utils/app.settings';
import { HttpService } from '@/app/core/services/http/http.service';

@Injectable({
  providedIn: 'root',
})
export class PoderCalorificoNominationService {
  poderCalorificoData: IPoderCalorificoResponse = [];
  options: IPoderCalorificoNominationOptions | null = null;

  constructor(
    private http: HttpService,
    private httpClient: HttpClient,
    private appSetting: AppSettings
  ) {}

  /**
   * Obtiene todos los datos de poder calorífico desde el estado local o del servidor
   */
  async getAllPoderCalorifico(
    query?: IPoderCalorificoNominationQuery
  ): Promise<IPoderCalorificoResponse> {
    // Si hay query params, siempre obtener del servidor
    if (query && Object.keys(query).length > 0) {
      const data = await this.updateLocalPoderCalorifico(query);
      this.poderCalorificoData = data;
      return data;
    }

    // Si hay datos en memoria, retornarlos
    if (this.poderCalorificoData && this.poderCalorificoData.length > 0) {
      return this.poderCalorificoData;
    }

    // Si no hay datos, obtener del servidor con fechas por defecto (15 días hacia atrás y 30 días hacia adelante)
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const fifteenDaysAgo = new Date(today);
    fifteenDaysAgo.setDate(today.getDate() - 15);

    const thirtyDaysAhead = new Date(today);
    thirtyDaysAhead.setDate(today.getDate() + 30);

    const defaultQuery: IPoderCalorificoNominationQuery = {
      fechaInicio: fifteenDaysAgo.toISOString().split('T')[0],
      fechaFin: thirtyDaysAhead.toISOString().split('T')[0],
    };

    const data = await this.updateLocalPoderCalorifico(defaultQuery);
    this.poderCalorificoData = data;
    return data;
  }

  /**
   * Actualiza los datos locales desde el servidor
   */
  async updateLocalPoderCalorifico(
    query?: IPoderCalorificoNominationQuery
  ): Promise<IPoderCalorificoResponse> {
    const url: string = this.appSetting.poderCalorificoNomination.urls.base;

    // Si no se proporcionan fechas, usar fechas por defecto (15 días hacia atrás y 30 días hacia adelante)
    let finalQuery = query;
    if (!query || !query.fechaInicio || !query.fechaFin) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const fifteenDaysAgo = new Date(today);
      fifteenDaysAgo.setDate(today.getDate() - 15);

      const thirtyDaysAhead = new Date(today);
      thirtyDaysAhead.setDate(today.getDate() + 30);

      finalQuery = {
        ...query,
        fechaInicio:
          query?.fechaInicio || fifteenDaysAgo.toISOString().split('T')[0],
        fechaFin:
          query?.fechaFin || thirtyDaysAhead.toISOString().split('T')[0],
      };
    }

    // Construir query params
    const params = new URLSearchParams();
    if (finalQuery.fechaInicio)
      params.append('fechaInicio', finalQuery.fechaInicio);
    if (finalQuery.fechaFin) params.append('fechaFin', finalQuery.fechaFin);
    if (finalQuery.transportadorUuid)
      params.append('transportadorUuid', finalQuery.transportadorUuid);
    if (finalQuery.puntoPoderCalorificoUuid)
      params.append(
        'puntoPoderCalorificoUuid',
        finalQuery.puntoPoderCalorificoUuid
      );

    const queryString = params.toString();
    const queryParams = queryString ? `?${queryString}` : '';

    const response = (await this.http.getService(
      `${url}${queryParams}`
    )) as IPoderCalorificoResponse;

    return response || [];
  }

  /**
   * Obtiene las opciones para combos (puntos + transportadores)
   */
  async getPoderCalorificoOptions(): Promise<IPoderCalorificoNominationOptions> {
    if (this.options) {
      return this.options;
    }

    const url: string = `${this.appSetting.poderCalorificoNomination.urls.base}/options`;
    this.options = (await this.http.getService(
      url
    )) as IPoderCalorificoNominationOptions;

    return this.options;
  }

  /**
   * Descarga el template para carga de archivos
   */
  async downloadTemplate(): Promise<Blob> {
    const url: string = `${this.appSetting.poderCalorificoNomination.urls.base}/template`;
    return await firstValueFrom(
      this.httpClient.get(url, { responseType: 'blob' })
    );
  }

  /**
   * Detecta si el error está relacionado con estructura/columnas faltantes del archivo
   * Columnas requeridas: Fecha dd/mm/aaaa, Punto de poder calorífico, Poder calorífico, Transportador
   */
  private isStructureError(error: any): boolean {
    // Obtener el mensaje de error de diferentes posibles ubicaciones
    const errorMessage = (
      error?.error?.message ||
      error?.message ||
      (typeof error?.error === 'string' ? error.error : '') ||
      JSON.stringify(error?.error || {}).toLowerCase() ||
      ''
    ).toLowerCase();

    // Nombres específicos de las columnas requeridas
    const requiredColumns = [
      'fecha dd/mm/aaaa',
      'fecha',
      'punto de poder calorífico',
      'punto poder calorifico',
      'poder calorífico',
      'poder calorifico',
      'transportador',
    ];

    // Verificar si el mensaje menciona alguna columna requerida faltante
    const mentionsRequiredColumn = requiredColumns.some(column =>
      errorMessage.includes(column)
    );

    // Palabras clave que indican error de estructura/columnas
    const structureKeywords = [
      'columna',
      'columnas',
      'estructura',
      'estructura requerida',
      'missing',
      'required',
      'faltan',
      'no cumple',
      'invalid structure',
      'missing column',
      'column missing',
      'required column',
      'campo requerido',
      'campo faltante',
      'invalid format',
      'formato inválido',
      'invalid file',
      'archivo inválido',
    ];

    // Verificar si el mensaje contiene palabras clave de estructura
    const hasStructureKeyword = structureKeywords.some(keyword =>
      errorMessage.includes(keyword)
    );

    // Si menciona una columna requerida o tiene palabras clave de estructura, es un error de estructura
    if (mentionsRequiredColumn || hasStructureKeyword) {
      return true;
    }

    // Si es un error 400 (Bad Request) y el mensaje está vacío o es genérico,
    // podría ser un error de estructura (validación de archivo)
    const isBadRequest = error?.status === 400 || error?.error?.status === 400;
    if (
      isBadRequest &&
      (!errorMessage || errorMessage.includes('bad request'))
    ) {
      return true;
    }

    return false;
  }

  /**
   * Sube un archivo de poder calorífico
   */
  async uploadPoderCalorifico(
    file: File
  ): Promise<IPoderCalorificoNominationUploadResponse> {
    const url: string = this.appSetting.poderCalorificoNomination.urls.upload;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const data = await firstValueFrom(
        this.httpClient.post<IPoderCalorificoNominationUploadResponse>(
          url,
          formData
        )
      );

      // Limpiar datos en memoria para forzar una nueva consulta después de cargar
      this.poderCalorificoData = [];

      return {
        success: true,
        message: 'Archivo subido correctamente',
        data: data.data || [],
      };
    } catch (err: any) {
      // Extraer errores detallados del backend si existen
      const backendErrors = err.error?.errors || [];
      const backendMessage =
        err.error?.message || err.message || 'Error al subir el archivo';

      // Si hay errores detallados, retornarlos junto con el mensaje general
      if (backendErrors.length > 0) {
        return {
          success: false,
          message: backendMessage,
          errors: backendErrors.map((error: any) => ({
            row: error.row || 0,
            message: error.message || error,
          })),
        };
      }

      // Si el error está relacionado con estructura/columnas faltantes, mostrar mensaje específico
      if (this.isStructureError(err)) {
        return {
          success: false,
          message: 'El archivo no cumple con la estructura requerida',
        };
      }

      return {
        success: false,
        message: backendMessage,
      };
    }
  }

  /**
   * Confirma valores de poder calorífico
   * @param fecha Fecha en formato 'YYYY-MM-DD' para confirmar
   */
  async confirmValues(
    fecha: string
  ): Promise<{ success: boolean; message?: string }> {
    const url: string = `${this.appSetting.poderCalorificoNomination.urls.base}/confirm`;

    try {
      await this.http.postService(url, { fecha });

      return {
        success: true,
        message: 'Valores confirmados correctamente',
      };
    } catch (err: any) {
      // Extraer el mensaje de error del objeto de respuesta HTTP
      // El error puede venir en diferentes formatos:
      // 1. err.error.details (mensaje del backend)
      // 2. err.error.message (mensaje genérico del backend)
      // 3. err.message (mensaje del error HTTP)
      // 4. err (si es un string directo)
      let errorMessage = 'Error al confirmar valores';

      if (err?.error) {
        // Error HTTP con estructura { error: { details: "...", message: "..." } }
        errorMessage = err.error.details || err.error.message || errorMessage;
      } else if (typeof err === 'string') {
        // Error como string directo
        errorMessage = err;
      } else if (err?.message) {
        // Error con propiedad message
        errorMessage = err.message;
      }

      return {
        success: false,
        message: errorMessage,
      };
    }
  }

  /**
   * Confirma múltiples fechas de poder calorífico
   * @param fechas Array de fechas en formato 'YYYY-MM-DD' para confirmar
   */
  async confirmMultipleValues(
    fechas: string[]
  ): Promise<{ success: boolean; message?: string; errors?: string[] }> {
    const errors: string[] = [];
    let successCount = 0;

    // Confirmar cada fecha individualmente
    for (const fecha of fechas) {
      try {
        const result = await this.confirmValues(fecha);
        if (result.success) {
          successCount++;
        } else {
          errors.push(`${fecha}: ${result.message}`);
        }
      } catch (error: any) {
        errors.push(`${fecha}: ${error.message || 'Error desconocido'}`);
      }
    }

    // Actualizar datos locales después de confirmar todas las fechas
    if (successCount > 0) {
      await this.updateLocalPoderCalorifico();
    }

    return {
      success: errors.length === 0,
      message:
        errors.length === 0
          ? `Se confirmaron ${successCount} fecha(s) correctamente`
          : `Se confirmaron ${successCount} fecha(s), pero hubo ${errors.length} error(es)`,
      errors: errors.length > 0 ? errors : undefined,
    };
  }

  /**
   * Limpia el estado local (útil para forzar recarga)
   */
  clearLocalData(): void {
    this.poderCalorificoData = [];
    this.options = null;
  }
}
